import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import { plainQuestion } from '@aytm/app/stats/page/data-quality-report/lib/format'
import style from '@aytm/app/stats/page/data-quality-report/styles/sections/SurveyPerformanceChart.module.scss'

const LINE_COLOR = '#2aa4d6'
const HIGHLIGHT_COLOR = '#f5a623'

// Pin the callout to the highlighted point (the default resting state).
const pinHighlight = (chart, highlightIndex) => {
  if (highlightIndex == null) return
  const point = chart?.series?.[0]?.points?.[highlightIndex]
  // onMouseOver runs the full hover routine, so the callout is positioned from the
  // point's own coordinates rather than a stale tooltip.refresh (which misplaces it).
  point?.onMouseOver()
}

// How many checkpoints the axis can name before the labels start colliding, and how close
// together two named checkpoints may sit.
const MAX_TICKS = 16
const MIN_TICK_GAP = 2

// Peaks cluster wherever the spikes are, so a funnel with two distant spikes names two distant
// checkpoints and leaves the flat stretch between them unreadable -- a reader landing mid-curve has
// no nearby label to count from. Whatever tick budget the peaks leave is spent on the widest gaps,
// each split at its midpoint, so the axis stays evenly named across its whole span.
const fillGaps = ticks => {
  while (MAX_TICKS > ticks.length) {
    const widest = ticks.reduce((at, t, k) => (0 < k && t - ticks[k - 1] > ticks[at] - ticks[at - 1] ? k : at), 1)
    const mid = Math.round((ticks[widest - 1] + ticks[widest]) / 2)
    // The widest gap is the only one that can still take a midpoint; once it cannot, none can.
    if (MIN_TICK_GAP > Math.min(mid - ticks[widest - 1], ticks[widest] - mid)) break
    ticks.splice(widest, 0, mid)
  }

  return ticks
}

// Which checkpoints the axis names. Left to itself Highcharts samples at a fixed step, so the
// named ones are whatever the step lands on -- the spikes, the only checkpoints a reader wants to
// identify, are as likely to be skipped as the flat tail. Peaks are named first instead, tallest
// down, and the two ends are always named: they also pin the axis extremes, which is what stops
// the trailing tick from falling past the last category and printing its raw index.
const peakTicks = series => {
  const last = series.length - 1
  if (0 > last) return
  if (MAX_TICKS >= series.length) return series.map((_, i) => i)

  const ticks = [0, last]
  series
    .map((y, i) => ({ y, i }))
    .filter(({ y, i }) => 0 < i && i < last && y > series[i - 1] && y >= series[i + 1])
    .sort((a, b) => b.y - a.y || a.i - b.i)
    .forEach(({ i }) => {
      if (MAX_TICKS <= ticks.length) return
      if (ticks.every(t => MIN_TICK_GAP <= Math.abs(t - i))) ticks.push(i)
    })

  return fillGaps(ticks.sort((a, b) => a - b))
}

// Drop-off curve: x = question checkpoints, y = respondents who dropped at that checkpoint.
const calcOptions = ({ categories, series, titles, highlightIndex }) => ({
  chart: {
    type: 'areaspline',
    height: 240,
    backgroundColor: 'transparent',
    spacing: [8, 0, 8, 0],
    events: {
      load() {
        const chart = this
        if (highlightIndex == null) return
        pinHighlight(chart, highlightIndex)
        // Re-pin via Highcharts' own leave detection: pointer.reset() fires whenever the
        // cursor leaves the series/chart. More reliable than a DOM mouseleave, which the
        // useHTML callout overlay can swallow when the cursor exits through it.
        chart.pointer.reset = () => pinHighlight(chart, highlightIndex)
      }
    }
  },
  title: { text: undefined },
  credits: { enabled: false },
  legend: { enabled: false },
  exporting: { enabled: false }, // no download/context-menu button on the chart
  xAxis: {
    categories,
    endOnTick: true,
    labels: { style: { color: '#6b7280', fontSize: '11px' } },
    lineColor: '#e5e7eb',
    // Default category padding leaves the curve floating off both edges; anchor it to the axes.
    minPadding: 0,
    maxPadding: 0,
    startOnTick: true,
    tickLength: 0,
    tickPositions: peakTicks(series)
  },
  yAxis: {
    title: { text: 'respondents', style: { color: '#9aa1ac', fontSize: '11px' } },
    min: 0,
    // Auto-fit the top to the tallest drop-off (headroom for the peak marker + pinned callout) so a
    // mostly-small funnel fills the panel instead of hugging the baseline; zero baseline kept.
    maxPadding: 0.15,
    gridLineColor: '#eef0f3',
    // Respondent counts are whole people, so a scale short enough to tick in fractions must not
    // label the same count twice.
    allowDecimals: false,
    labels: { style: { color: '#6b7280', fontSize: '11px' } }
  },
  // Styled as the design callout; the title names the question, the body its drop-off count.
  tooltip: {
    enabled: true,
    useHTML: true,
    shape: 'callout',
    hideDelay: 0,
    backgroundColor: '#fff',
    borderColor: '#e5e7eb',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    shadow: { color: 'rgba(31, 42, 82, 0.12)', offsetX: 0, offsetY: 0, width: 6, opacity: 0.5 },
    // Stripped, then escaped: the label is author-written question text. Highcharts' AST strips script
    // vectors from useHTML but still parses markup, so an <img>/<a> in a question would otherwise
    // render as an element — and escaping alone would show its tags mid-sentence instead.
    formatter() {
      const label = this.point?.custom?.label ?? this.key
      const title = this.point?.custom?.title
      const name = plainQuestion(label)
      // The checkpoint label leads in bold and the question's own wording previews after it, clamped
      // to two lines by the callout style so a long question ellipses instead of growing the bubble.
      const heading = title && title !== label ? `<b>${name}:</b> ${plainQuestion(title)}` : `<b>${name}</b>`
      return `<div class="${style.calloutTitle}">${heading}</div><div class="${style.calloutText}">${this.y} dropped</div>`
    }
  },
  plotOptions: {
    areaspline: {
      color: LINE_COLOR,
      clip: false, // first/last markers now sit on the axes and would be sliced in half by the plot clip
      fillOpacity: 0.12,
      lineWidth: 2,
      marker: { enabled: true, radius: 4, fillColor: '#fff', lineColor: LINE_COLOR, lineWidth: 2 }
    }
  },
  series: [
    {
      name: 'Respondents',
      data: series.map((y, i) => {
        const point = { y, custom: { label: categories[i], title: titles[i] ?? categories[i] } }
        return i === highlightIndex
          ? { ...point, marker: { radius: 7, fillColor: HIGHLIGHT_COLOR, lineColor: HIGHLIGHT_COLOR, lineWidth: 2 } }
          : point
      })
    }
  ]
})

const SurveyPerformanceChart = props => (
  <div data-testid="stat-dqr-survey-performance-chart" className={style.chart}>
    <HighchartsReact highcharts={Highcharts} options={calcOptions(props)} />
  </div>
)

SurveyPerformanceChart.displayName = 'SurveyPerformanceChart'
SurveyPerformanceChart.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string),
  series: PropTypes.arrayOf(PropTypes.number),
  titles: PropTypes.arrayOf(PropTypes.string),
  highlightIndex: PropTypes.number
}
SurveyPerformanceChart.defaultProps = { categories: [], series: [], titles: [] }
export default SurveyPerformanceChart
